import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { EnvelopeSimple, CircleNotch, LockSimple, Phone } from "@phosphor-icons/react";
import { postContact, AGENCY } from "@/lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "", consent: false });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return toast.error("Please accept the consent to continue.");
    setLoading(true);
    try {
      await postContact(form);
      toast.success("Message sent — we'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "", consent: false });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const details = [
    { icon: Phone, label: "Call us", value: AGENCY.phone, href: AGENCY.phoneHref },
    { icon: EnvelopeSimple, label: "Email", value: AGENCY.email, href: `mailto:${AGENCY.email}` },
  ];

  return (
    <section id="contact" className="bg-warm-white py-24 lg:py-32">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <p className="eyebrow text-gold mb-4 flex items-center gap-3">
            <span className="inline-block w-8 h-px bg-gold" /> Contact
          </p>
          <h2 className="font-heading font-extrabold tracking-tighter text-navy text-[clamp(2rem,4.5vw,3.4rem)] leading-[1]">
            Talk to a licensed agent.
          </h2>
          <p className="mt-5 text-brand-slate leading-relaxed max-w-md">
            Questions before you commit? Reach out directly — no call center, no runaround.
          </p>

          <div className="mt-10 space-y-4">
            {details.map((d) => {
              const Icon = d.icon;
              const inner = (
                <div className="flex items-center gap-4 rounded-xl border border-black/[0.07] bg-white p-4 transition-all hover:border-navy/30">
                  <span className="grid place-items-center w-11 h-11 rounded-lg bg-navy/[0.06] text-navy">
                    <Icon weight="duotone" size={22} />
                  </span>
                  <div>
                    <p className="text-[11px] uppercase tracking-[0.18em] text-light-slate">{d.label}</p>
                    <p className="text-navy font-semibold text-sm mt-0.5">{d.value}</p>
                  </div>
                </div>
              );
              return d.href ? (
                <a key={d.label} href={d.href} data-testid={`contact-${d.label.toLowerCase().replace(/\s/g, "-")}`}>{inner}</a>
              ) : (
                <div key={d.label} data-testid={`contact-${d.label.toLowerCase().replace(/\s/g, "-")}`}>{inner}</div>
              );
            })}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }} transition={{ duration: 0.6 }}
          className="lg:col-span-7 rounded-2xl border border-black/[0.07] bg-white p-7 sm:p-9"
        >
          <div className="flex items-center gap-2 text-brand-slate mb-6">
            <LockSimple weight="fill" size={16} className="text-gold" />
            <span className="text-xs">Your details are kept private and never sold.</span>
          </div>
          <form onSubmit={submit} data-testid="contact-form" className="grid sm:grid-cols-2 gap-4">
            <Input label="Full name" testid="contact-name" value={form.name} onChange={(v) => set("name", v)} required />
            <Input label="Phone" type="tel" testid="contact-phone" value={form.phone} onChange={(v) => set("phone", v)} />
            <Input label="Email" type="email" testid="contact-email" value={form.email} onChange={(v) => set("email", v)} required className="sm:col-span-2" />
            <Input label="Subject" testid="contact-subject" value={form.subject} onChange={(v) => set("subject", v)} className="sm:col-span-2" />
            <div className="sm:col-span-2">
              <label className="block text-[11px] uppercase tracking-[0.18em] text-brand-slate mb-1.5">Message</label>
              <textarea
                data-testid="contact-message"
                value={form.message}
                required
                rows={4}
                onChange={(e) => set("message", e.target.value)}
                className="w-full rounded-lg border border-black/10 bg-white px-4 py-3 text-navy text-sm outline-none focus:border-navy transition-colors resize-none"
              />
            </div>
            <label className="sm:col-span-2 flex items-start gap-3 cursor-pointer">
              <input type="checkbox" data-testid="contact-consent" checked={form.consent}
                onChange={(e) => set("consent", e.target.checked)} className="mt-1 h-4 w-4 shrink-0 accent-navy" />
              <span className="text-[12px] leading-relaxed text-brand-slate">
                By clicking submit, you authorize a licensed agent to contact you via phone or email regarding insurance options.
              </span>
            </label>
            <button
              type="submit"
              data-testid="contact-submit"
              disabled={loading}
              className="sm:col-span-2 inline-flex items-center justify-center gap-2 rounded-full bg-navy px-6 py-3.5 text-warm-white font-semibold transition-all hover:bg-slate-navy disabled:opacity-60 active:scale-[0.98]"
            >
              {loading ? <CircleNotch className="animate-spin" size={20} /> : "Send message"}
            </button>
          </form>
        </motion.div>
      </div>
    </section>
  );
}

function Input({ label, value, onChange, type = "text", testid, required, className = "" }) {
  return (
    <div className={className}>
      <label className="block text-[11px] uppercase tracking-[0.18em] text-brand-slate mb-1.5">{label}</label>
      <input
        type={type} data-testid={testid} value={value} required={required}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-black/10 bg-white px-4 py-3 text-navy text-sm outline-none focus:border-navy transition-colors"
      />
    </div>
  );
}
